// src/pages/CartPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CartContext } from '../context/CartContext';
import CheckoutModal from '../components/checkout/CheckoutModal';

function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateCartItemCount } = useContext(CartContext) || {};

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subTotal, setSubTotal] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);

  // Calcul du prix après réduction
  const getEffectivePrice = (product) => {
    const original = Number(product?.prix ?? 0);
    let discount = Number(product?.discount ?? 0);

    const newPrice =
      product?.new_price != null
        ? Number(product.new_price)
        : null;

    if (discount > 0 && newPrice != null) {
      return newPrice;
    }

    if (discount > 0 && discount <= 1) {
      discount *= 100;
    }

    if (discount > 0 && original > 0) {
      return Math.round(original * (1 - discount / 100));
    }

    return original;
  };

  // Vérification de la promotion
  const getHasPromo = (product) => {
    let discount = Number(product?.discount ?? 0);

    if (discount > 0 && discount <= 1) {
      discount *= 100;
    }

    const newPrice =
      product?.new_price != null
        ? Number(product.new_price)
        : null;

    return (
      discount > 0 &&
      (newPrice != null || Number(product?.prix ?? 0) > 0)
    );
  };

  // Construction de l'URL de l'image
  const getImageUrl = (image) => {
  if (!image || typeof image !== 'string') {
    return '';
  }

  const clean = image.trim();

  // URL complète
  if (/^https?:\/\//i.test(clean)) {
    return clean;
  }

  // Récupérer le nom du fichier
  const filename = clean.split('/').pop();

  if (!filename) {
    return '';
  }

  return `https://madadline.com/api/product-images/${encodeURIComponent(filename)}`;
};

  // Chargement du panier
  useEffect(() => {
  let isMounted = true;

  const fetchCart = async () => {
    setLoading(true);
    const token = localStorage.getItem('auth_token');

    // ⭐ Plus de blocage si pas de token : on charge depuis localStorage
    try {
      let items = [];

      if (token) {
        // Client connecté : panier depuis le serveur
        const [cartRes, totalRes] = await Promise.all([
          axios.get('https://madadline.com/api/cart', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('https://madadline.com/api/cart/total', {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { delivery_fee: 0 } })),
        ]);

        items = Array.isArray(cartRes.data) ? cartRes.data : [];
        const { delivery_fee = 0 } = totalRes.data || {};
        setDeliveryFee(delivery_fee);
      } else {
        // Visiteur non connecté : panier depuis localStorage
        try {
          items = JSON.parse(localStorage.getItem('cart') || '[]');
        } catch {
          items = [];
        }
        setDeliveryFee(0);
      }

      if (!isMounted) return;
      setCartItems(items);

      // Recalcul local du sous-total
      const localSubTotal = items.reduce((acc, item) => {
        return acc + getEffectivePrice(item.product) * item.quantity;
      }, 0);
      setSubTotal(localSubTotal);

      if (typeof updateCartItemCount === 'function') updateCartItemCount(items);
    } catch (err) {
      if (!isMounted) return;
      setError('Erreur lors du chargement du panier.');
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  fetchCart();
  return () => { isMounted = false; };
}, [navigate, updateCartItemCount]);
  // Recalcul du sous-total
  const recalcTotal = (items) => {
    const sub = items.reduce((acc, item) => {
      const price = getEffectivePrice(item.product);

      return acc + price * Number(item.quantity || 0);
    }, 0);

    setSubTotal(sub);
  };

  // Modification de la quantité
  const handleQuantityChange = async (itemId, newQty) => {
    if (newQty < 1) return;

    const token = localStorage.getItem('auth_token');
    const item = cartItems.find((i) => i.id === itemId);

    if (!item) return;

    try {
      await axios.put(
        `https://madadline.com/api/cart/item/${itemId}`,
        {
          quantity: newQty,
          size: item.size,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updated = cartItems.map((i) =>
        i.id === itemId
          ? { ...i, quantity: newQty }
          : i
      );

      setCartItems(updated);
      localStorage.setItem('cart', JSON.stringify(updated));

      if (typeof updateCartItemCount === 'function') {
        updateCartItemCount(updated);
      }

      recalcTotal(updated);
    } catch (err) {
      console.error(
        'Erreur de modification de quantité :',
        err
      );
    }
  };

  // Suppression d'un article
  const handleDeleteItem = async (itemId) => {
    const token = localStorage.getItem('auth_token');

    try {
      await axios.delete(
        `https://madadline.com/api/cart/item/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updated = cartItems.filter(
        (item) => item.id !== itemId
      );

      setCartItems(updated);
      localStorage.setItem('cart', JSON.stringify(updated));

      if (typeof updateCartItemCount === 'function') {
        updateCartItemCount(updated);
      }

      recalcTotal(updated);
    } catch (err) {
      console.error('Erreur de suppression :', err);
    }
  };

  // Vider le panier
  const handleClearCart = async () => {
    if (!window.confirm(t('cart.confirmClear'))) {
      return;
    }

    const token = localStorage.getItem('auth_token');

    try {
      await axios.delete(
        'https://madadline.com/api/cart/clear',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCartItems([]);
      setSubTotal(0);
      localStorage.removeItem('cart');

      if (typeof updateCartItemCount === 'function') {
        updateCartItemCount([]);
      }
    } catch (err) {
      console.error('Erreur lors du vidage du panier :', err);
    }
  };

  // Paiement réussi
  const handlePaymentSuccess = () => {
    setCartItems([]);
    setSubTotal(0);
    setDeliveryFee(0);

    localStorage.removeItem('cart');

    if (typeof updateCartItemCount === 'function') {
      updateCartItemCount([]);
    }

    setShowCheckout(false);
    navigate('/');
  };

  const totalItems = cartItems.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0
  );

  // Économies réalisées
  const totalSavings = cartItems.reduce((acc, item) => {
    if (!getHasPromo(item.product)) {
      return acc;
    }

    const original = Number(item.product?.prix || 0);
    const promo = getEffectivePrice(item.product);

    return (
      acc +
      (original - promo) * Number(item.quantity || 0)
    );
  }, 0);

  // Chargement
  if (loading) {
    return (
      <main className="cart-page">
        <div className="cart-page__loading">
          <div className="spinner" />
        </div>
      </main>
    );
  }

  // Erreur ou utilisateur non connecté
  if (error) {
    return (
      <main className="cart-page">
        <div className="cart-page__empty">
          <h2>{t('cart.inaccessible')}</h2>
          <p>{error}</p>

          <Link to="/login" className="btn btn-primary">
            {t('cart.loginBtn')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__head">
        <span className="cart-page__label">
          {t('cart.heroLabel')}
        </span>

        <h1 className="cart-page__title">
          {t('cart.heroTitle')}
        </h1>

        {totalItems > 0 && (
          <p className="cart-page__count">
            {totalItems}{' '}
            {totalItems > 1
              ? t('cart.articles')
              : t('cart.article')}
          </p>
        )}
      </header>

      {cartItems.length === 0 ? (
        <section className="cart-page__empty-section">
          <div className="cart-page__empty">
            <div className="cart-page__empty-icon">🛍</div>

            <h2>{t('cart.emptyTitle')}</h2>
            <p>{t('cart.emptyText')}</p>

            <Link to="/boutique" className="btn btn-primary">
              {t('cart.discoverShop')}
            </Link>
          </div>
        </section>
      ) : (
        <section className="cart-page__content">
          <div className="cart-page__items">
            {cartItems.map((item) => {
              const p = item.product || {};

              const img = getImageUrl(p.image);
              const name = p.nom || p.name || '';

              const hasPromo = getHasPromo(p);
              const originalPrice = Number(p.prix || 0);
              const effectivePrice = getEffectivePrice(p);
              const subtotal =
                effectivePrice * Number(item.quantity || 0);

              let discount = Number(p.discount || 0);

              if (discount > 0 && discount <= 1) {
                discount = Math.round(discount * 100);
              }

              return (
                <article key={item.id} className="cart-item">
                  <Link
                    to={`/product/${p.id}`}
                    className="cart-item__img"
                  >
                    <img
                      src={img}
                      alt={name || 'Produit'}
                      onError={(e) => {
                        console.error(
                          'Image du panier non chargée :',
                          e.currentTarget.src
                        );
                        e.currentTarget.onerror = null;
                      }}
                    />

                    {hasPromo && (
                      <span className="cart-item__promo-badge">
                        -{discount}%
                      </span>
                    )}
                  </Link>

                  <div className="cart-item__info">
                    <Link
                      to={`/product/${p.id}`}
                      className="cart-item__name"
                    >
                      {name}
                    </Link>

                    <div className="cart-item__meta">
                      {item.size && (
                        <span>
                          {t('cart.size')} : {item.size}
                        </span>
                      )}

                      {item.color && (
                        <span>
                          {t('cart.color')} : {item.color}
                        </span>
                      )}

                      {item.material && (
                        <span>
                          {t('cart.material')} : {item.material}
                        </span>
                      )}
                    </div>

                    {hasPromo ? (
                      <div className="cart-item__price-promo">
                        <span className="cart-item__price-new">
                          {effectivePrice.toFixed(0)} DH
                        </span>

                        <span className="cart-item__price-old">
                          {originalPrice.toFixed(0)} DH
                        </span>

                        <span className="cart-item__price-save">
                          {t('cart.save')}{' '}
                          {(originalPrice - effectivePrice).toFixed(0)} DH
                        </span>
                      </div>
                    ) : (
                      <div className="cart-item__price-unit">
                        {originalPrice.toFixed(0)}{' '}
                        {t('cart.pricePerPiece')}
                      </div>
                    )}
                  </div>

                  <div className="cart-item__qty">
                    <button
                      className="cart-item__qty-btn"
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          Number(item.quantity) - 1
                        )
                      }
                      disabled={Number(item.quantity) <= 1}
                    >
                      −
                    </button>

                    <span className="cart-item__qty-value">
                      {item.quantity}
                    </span>

                    <button
                      className="cart-item__qty-btn"
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          Number(item.quantity) + 1
                        )
                      }
                    >
                      +
                    </button>
                  </div>

                  <div className="cart-item__subtotal">
                    {subtotal.toFixed(0)} DH
                  </div>

                  <button
                    className="cart-item__remove"
                    onClick={() => handleDeleteItem(item.id)}
                    aria-label={t('cart.removeAria')}
                  >
                    ✕
                  </button>
                </article>
              );
            })}

            <div className="cart-page__items-actions">
              <Link
                to="/boutique"
                className="cart-page__continue"
              >
                ← {t('cart.continueShopping')}
              </Link>

              <button
                className="cart-page__clear"
                onClick={handleClearCart}
              >
                {t('cart.clearCart')}
              </button>
            </div>
          </div>

          <aside className="cart-page__summary">
            <div className="cart-page__summary-box">
              <h2 className="cart-page__summary-title">
                {t('cart.summaryTitle')}
              </h2>

              <div className="cart-page__summary-row">
                <span>{t('cart.subtotal')}</span>
                <span>{subTotal.toFixed(0)} DH</span>
              </div>

              {totalSavings > 0 && (
                <div className="cart-page__summary-row cart-page__summary-row--save">
                  <span>{t('cart.youSave')}</span>
                  <span>-{totalSavings.toFixed(0)} DH</span>
                </div>
              )}

              <div className="cart-page__summary-row">
                <span>{t('cart.shipping')}</span>

                <span>
                  {deliveryFee > 0
                    ? `${deliveryFee.toFixed(0)} DH`
                    : t('cart.toCalculate')}
                </span>
              </div>

              <div className="cart-page__summary-divider" />

              <div className="cart-page__summary-row cart-page__summary-row--total">
                <span>{t('cart.total')}</span>
                <span>
                  {(subTotal + deliveryFee).toFixed(0)} DH
                </span>
              </div>

              <button
                className="cart-page__checkout-btn"
                onClick={() => setShowCheckout(true)}
              >
                {t('cart.checkout')}
              </button>

              <ul className="cart-page__reassure">
                <li>{t('cart.reassure1')}</li>
                <li>{t('cart.reassure2')}</li>
                <li>{t('cart.reassure3')}</li>
                <li>{t('cart.reassure4')}</li>
              </ul>
            </div>
          </aside>
        </section>
      )}

      {showCheckout && (
        <CheckoutModal
          cartItems={cartItems}
          subTotal={subTotal}
          deliveryFee={deliveryFee}
          onClose={() => setShowCheckout(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </main>
  );
}

export default CartPage;